import { SceneEmptyState } from './sceneEmptyState';
import styled, { css, keyframes } from 'styled-components';
import { treeRowRing, treeRowWash, treeTrailInk } from '../treeRowLook';

// LE TESSERE DELLA LIBRERIA. Il guscio — comandi, ricerca, filtro — è di tutte le sezioni e
// vive in `sectionPanel`; qui c'è solo come si mostra un asset: una tessera con la sua
// anteprima, il nome e, quando c'è, la riga tecnica.

export const AssetGroupTitle = styled.h4`
    margin: 12px 0 8px;
    color: ${(props) => props.theme.app.primary};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;

    &:first-child {
        margin-top: 0;
    }
`;

// La griglia va a capo; l'elenco no. In una colonna alta e stretta una riga che va a capo
// aprirebbe una seconda colonna da leggere di lato, che è il contrario di un elenco.
// IN GRIGLIA UN GRUPPO È UNA RIGA A TUTTA LARGHEZZA, non una tessera fra le tessere. Una
// scheda in fila con il proprio contenuto sembra un fratello, e i materiali che vengono dopo non
// si distinguono dagli sciolti; una testata che attraversa le colonne li apre invece come una
// sezione — sotto di lei ci sono i suoi, fino alla fascia successiva.
//
// È LA STESSA RIGA DELL'ALBERO, con gli stessi pezzi nello stesso ordine — freccetta, glifo,
// nome, conteggio — perché è la stessa cosa guardata in un altro modo. Quello che cambia è che
// qui il rientro non esiste: aprire e chiudere è il solo modo che la griglia ha di dire «questi
// sono suoi».
// UNA RIGA SCELTA SI ACCENDE, NON SI RIEMPIE. Era una fascia arancione piena con il nome in
// bianco: un colore che nel resto di questo editor vuol dire «premi qui», usato per dire
// «questa». Il segno giusto è quello che l'albero dà già alle sue righe — un velo della tinta
// e un filo attorno — perché così la riga resta leggibile, la cartella tiene il suo viola, e
// una selezione non urla più di un comando.
// LA TESTATA DI UN GRUPPO IN GRIGLIA È UNA RIGA DELL'ELENCO (prototipo, .lrow.group-row; utente 2026-10-21): scelta,
// lavato e filo arancio; con dentro la scelta (la «scia»), il lavato e la cartella in arancio da testo.
export const AssetGroupRow = styled.div<{ $selected?: boolean; $trail?: boolean }>`
    display: flex;
    align-items: center;
    gap: 6px;
    min-height: 30px;
    margin: 12px 0 6px;
    padding: 0 6px;
    border-radius: 8px;
    background: ${(props) => (props.$selected || props.$trail ? treeRowWash(props.theme) : 'transparent')};
    box-shadow: ${(props) => (props.$selected ? treeRowRing(props.theme) : 'none')};
    color: var(--editor-muted, #55595f);

    &:first-child {
        margin-top: 0;
    }

    &:hover {
        background: ${(props) => (props.$selected || props.$trail ? treeRowWash(props.theme) : 'var(--editor-row-hover, #f1f2f4)')};
    }

    ${(props) =>
        (props.$selected || props.$trail) &&
        css`
            color: ${treeTrailInk(props.theme)};
        `}
`;

// La cartella prende il colore della riga: grigia, arancio da testo sulla scia (come nell'elenco).
export const AssetGroupGlyph = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    color: inherit;

    svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
`;

// IL NOME È IL COMANDO che apre la scheda del gruppo, e prende tutta la riga che avanza: la
// freccetta accanto ne è un altro, e due pulsanti annidati non si possono fare.
export const AssetGroupName = styled.button`
    flex: 1 1 auto;
    min-width: 0;
    border: 0;
    padding: 0;
    background: transparent;
    color: var(--editor-fg, #262a30);
    font-family: inherit;
    font-size: 12px;
    font-weight: 400;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid ${(props) => props.theme.app.secondary};
        outline-offset: 2px;
        border-radius: 3px;
    }
`;

// IL CONTEGGIO È QUELLO DICHIARATO, non quello mostrato: un gruppo dice quanti materiali ha, e un
// filtro acceso non gliene toglie nessuno.
export const AssetGroupCount = styled.span`
    flex: none;
    color: ${(props) => props.theme.app.primary};
    font-size: 10px;
    font-variant-numeric: tabular-nums;
`;

export const AssetGrid = styled.div<{ $list?: boolean }>`
    display: grid;
    /* Anteprime più piccole (prototipo, .items.grid minmax 72px; utente 2026-10-21): una colonna in più a parità di pannello. */
    grid-template-columns: ${(props) => (props.$list ? '1fr' : 'repeat(auto-fill, minmax(72px, 1fr))')};
    gap: ${(props) => (props.$list ? '8px' : '6px')};
    align-content: start;
`;

// LA TESSERA È UNA CORNICE, NON UN PULSANTE. Finché il comando era uno solo — «aprimi» — la
// tessera poteva ESSERE quel pulsante. Con il «…» in un angolo diventerebbe un pulsante dentro un
// pulsante: l'HTML non lo ammette, e per chi naviga da tastiera il secondo comando semplicemente
// non esisterebbe. Quindi la cornice tiene bordo, sfondo e stato, e dentro ci stanno due comandi
// separati — la superficie che apre e il menu che agisce.
// «DOVE STA?» ANCHE IN GRIGLIA: la tessera lampeggia come lampeggia una riga dell'elenco — tre
// colpi, lo stesso colore — perché è la stessa risposta data in un'altra vista.
const blinkCard = keyframes`
    0%, 49.9% { background: var(--card-blink-hot); }
    50%, 100% { background: transparent; }
`;

export const AssetCard = styled.div<{ $selected?: boolean; $list?: boolean; $blinking?: boolean }>`
    --card-blink-hot: ${(props) => props.theme.app.secondary}66;

    position: relative;
    display: flex;
    min-width: 0;
    border: 1px solid ${(props) => (props.$selected ? props.theme.app.secondary : 'var(--editor-line, #d1d3d8)')};
    border-radius: 8px;
    /* LA TESSERA STA SUL TONO INCASSATO (prototipo, card): sul fondo del pannello si legge come una cosa, non
       solo come un bordo. Scelta è un lavato, non l'arancio pieno. */
    background: ${(props) => (props.$selected ? 'var(--editor-accent-wash, rgb(232 101 10 / 8%))' : 'var(--editor-sunken, #e9ebed)')};
    /* Scelta, il filo diventa di due pixel (prototipo, variante morbida 19/10): il secondo è dentro, così la tessera non cresce. */
    box-shadow: ${(props) => (props.$selected ? `inset 0 0 0 1px ${props.theme.app.secondary}` : 'none')};

    /* IL FUOCO SI VEDE SULLA CORNICE, non sul pezzo che lo tiene: dentro ci sono due comandi, e
       due contorni annidati direbbero «sei in due posti insieme». Il passaggio è neutro. */
    &:hover {
        border-color: ${(props) => (props.$selected ? props.theme.app.secondary : 'var(--editor-chapter, #84878c)')};
        background: ${(props) => (props.$selected ? 'var(--editor-accent-wash, rgb(232 101 10 / 8%))' : 'var(--editor-hover, #e3e5e8)')};
    }

    &:focus-within {
        border-color: ${(props) => props.theme.app.secondary};
    }

    ${(props) =>
        props.$blinking &&
        css`
            animation: ${blinkCard} 360ms steps(1, end) 3;
        `}
`;

export const AssetCardSurface = styled.button<{ $list?: boolean }>`
    display: flex;
    flex-direction: ${(props) => (props.$list ? 'row' : 'column')};
    align-items: ${(props) => (props.$list ? 'center' : 'stretch')};
    gap: ${(props) => (props.$list ? '10px' : '0')};
    flex: 1 1 auto;
    min-width: 0;
    padding: 6px ${(props) => (props.$list ? '8px' : '6px')};
    border: 0;
    border-radius: inherit;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;

    &:focus-visible {
        outline: none;
    }
`;

// IL «…» STA SOPRA LA TESSERA, in un angolo, e compare al passaggio, al fuoco e finché il suo menu è aperto
// (prototipo V3, `.reveal.float`): dodici tessere con dodici «…» accesi erano dodici richiami per un comando.
// SU UNO SCHERMO CHE SI TOCCA RESTA SEMPRE IN VISTA: lì il passaggio non esiste, e un comando che compare solo
// passandoci sopra non si troverebbe affatto.
export const AssetCardActions = styled.div`
    position: absolute;
    top: 8px;
    right: 8px;
    opacity: 0;

    ${AssetCard}:hover &,
    ${AssetCard}:focus-within &,
    &:has([data-open]) {
        opacity: 1;
    }

    @media (hover: none) {
        opacity: 1;
    }
`;

// UNA SFERA È LARGA QUANTO È ALTA. Con l'altezza fissa e la larghezza a percentuale usciva
// un'ellisse, e un materiale su un ovale non si legge: la sfera è il pezzo di geometria che
// mostra insieme luce diretta, riflesso e ombra propria.
export const AssetSwatch = styled.span<{ $list?: boolean; $round?: boolean }>`
    position: relative;
    display: block;
    flex: 0 0 auto;
    /* La sfera al 78% della tessera, al centro (prototipo): intorno resta l'aria che la separa dalla vicina. */
    width: ${(props) => (props.$list ? '34px' : '78%')};
    align-self: center;
    aspect-ratio: 1 / 1;
    border-radius: ${(props) => (props.$round ? '50%' : '6px')};
    background-color: ${(props) => props.theme.app.backgroundToolbar};
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    overflow: hidden;
`;

// IL NOME VA SU DUE RIGHE (prototipo V3): in una tessera da 80px una riga sola tagliava quasi ogni nome a metà
// della seconda parola, e due materiali «Leather bl…» non si distinguevano più.
export const AssetName = styled.span`
    display: -webkit-box;
    overflow: hidden;
    min-height: calc(2 * 14px);
    margin-top: 4px;
    color: ${(props) => props.theme.app.textColor};
    font-size: 11px;
    line-height: 14px;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
`;

// IL CAMPO EREDITA IL CARATTERE DEL NOME e restituisce lo spazio della propria cornice, o la
// tessera si allargherebbe nel momento in cui la si tocca.
export const AssetNameInput = styled.input`
    display: block;
    width: 100%;
    min-width: 0;
    margin: 6px -3px 0;
    padding: 1px 2px;
    border: 1px solid ${(props) => props.theme.app.secondary};
    border-radius: 4px;
    background: ${(props) => props.theme.app.backgroundToolbar};
    color: ${(props) => props.theme.app.textColor};
    font: inherit;
    font-size: 11px;
    text-align: center;
    outline: none;
`;

export const AssetMeta = styled.span`
    display: block;
    overflow: hidden;
    margin-top: 2px;
    color: ${(props) => props.theme.app.primary};
    font-size: 10px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

/* La griglia risponde come le liste: glifo, una riga, stessa misura. */
export const AssetEmptyState = SceneEmptyState;
